import { useEffect, useMemo, useState } from "react";
import { config } from "../config";

type Draft = {
  broadcastId: string;
  caption: string;
  facebookText: string;
  xText: string;
  youtubeTitle: string;
  youtubeDescription: string;
  liveUrl: string;
};

type Props = {
  title: string;
  locationLabel: string;
  broadcastId: string | null;
  captureImage?: () => Promise<File | null>;
};

function fallbackDraft(title: string, locationLabel: string, broadcastId: string) {
  const streamName = title.trim() || "Aruba Tourist Channel Live";
  const place = locationLabel ? ` at ${locationLabel}` : "";
  const base = `${streamName}${place}`;
  const caption = `${base}. Live broadcast ID ${broadcastId}. Watch live at ${config.livePageUrl}`;
  return {
    broadcastId,
    caption,
    facebookText: caption,
    xText: `${base}. Live now at ${config.livePageUrl}`,
    youtubeTitle: `${streamName} live`,
    youtubeDescription: `${base}\nBroadcast ID: ${broadcastId}\nWatch live: ${config.livePageUrl}`,
    liveUrl: config.livePageUrl,
  };
}

async function fetchDraft(title: string, locationLabel: string, broadcastId: string): Promise<Draft> {
  try {
    const res = await fetch(`${config.gatewayUrl.replace(/\/$/, "")}/social/compose`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, locationLabel, broadcastId }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data?.ok && typeof data.caption === "string") {
          return {
            broadcastId: String(data.broadcastId ?? broadcastId),
            caption: data.caption,
            facebookText: String(data.facebookText ?? data.caption),
            xText: String(data.xText ?? data.caption),
            youtubeTitle: String(data.youtubeTitle ?? title),
            youtubeDescription: String(data.youtubeDescription ?? data.caption),
            liveUrl: String(data.liveUrl ?? config.livePageUrl),
          };
        }
    }
  } catch {
    // fall back below
  }
  return fallbackDraft(title, locationLabel, broadcastId);
}

export default function ShareBar({ title, locationLabel, broadcastId, captureImage }: Props) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(false);

  const readyBroadcastId = useMemo(() => broadcastId ?? "", [broadcastId]);

  useEffect(() => {
    if (!readyBroadcastId) {
      setDraft(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoadingDraft(true);
      void fetchDraft(title, locationLabel, readyBroadcastId)
        .then((next) => {
          if (!cancelled) setDraft(next);
        })
        .finally(() => {
          if (!cancelled) setLoadingDraft(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [locationLabel, readyBroadcastId, title]);

  const activeDraft = draft ?? fallbackDraft(title, locationLabel, readyBroadcastId || `brd-${Date.now().toString(36)}`);

  const shareWithImage = async () => {
    setSharing(true);
    try {
      const file = captureImage ? await captureImage() : null;
      const canShareFiles =
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function" &&
        typeof window !== "undefined" &&
        "canShare" in navigator &&
        (!file || (navigator as Navigator & { canShare?: (data: ShareData) => boolean }).canShare?.({ files: [file] }) !== false);

      if (file && canShareFiles) {
        await navigator.share({
          title: activeDraft.youtubeTitle,
          text: activeDraft.caption,
          url: activeDraft.liveUrl,
          files: [file],
        });
        return;
      }

      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title: activeDraft.youtubeTitle,
          text: activeDraft.caption,
          url: activeDraft.liveUrl,
        });
        return;
      }

      await navigator.clipboard.writeText(`${activeDraft.caption}\n${activeDraft.liveUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } finally {
      setSharing(false);
    }
  };

  const copyText = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt(label, text);
    }
  };

  return (
    <div className="share-bar" aria-label="Share this stream">
      <span className="share-bar__label">Share:</span>
      <button
        className="share-bar__btn share-bar__btn--fb"
        onClick={() => void shareWithImage()}
        disabled={sharing || loadingDraft || !readyBroadcastId}
        title={readyBroadcastId ? `Broadcast ID ${readyBroadcastId}` : "Waiting for broadcast ID"}
      >
        {sharing ? "Preparing…" : loadingDraft ? "Drafting…" : "Share post"}
      </button>
      <button
        className="share-bar__btn share-bar__btn--x"
        onClick={() => void copyText("Copy X caption", activeDraft.xText)}
        disabled={!readyBroadcastId}
      >
        Copy X text
      </button>
      <button
        className="share-bar__btn share-bar__btn--wa"
        onClick={() => void copyText("Copy Facebook caption", activeDraft.facebookText)}
        disabled={!readyBroadcastId}
      >
        Copy Facebook
      </button>
      <button
        className={`share-bar__btn share-bar__btn--copy ${copied ? "is-copied" : ""}`}
        onClick={() => void copyText("Copy YouTube draft", `${activeDraft.youtubeTitle}\n\n${activeDraft.youtubeDescription}`)}
        disabled={!readyBroadcastId}
      >
        {copied ? "✓ Copied" : "Copy YouTube"}
      </button>
      <button
        className="share-bar__btn"
        onClick={() => void copyText("Copy live link", `${activeDraft.caption}\n${activeDraft.liveUrl}`)}
        disabled={!readyBroadcastId}
      >
        Copy link
      </button>
      {readyBroadcastId && (
        <span className="share-bar__label share-bar__label--id">
          ID: {readyBroadcastId}
        </span>
      )}
    </div>
  );
}
